import {
  ExtranetCustomerGroupDto,
  ExtranetCustomerGroupScheduleSlotDto,
} from '@pharmac-extranet/extranet-api'
import {
  CheckIcon,
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacBadge, PharmacIcon, PharmacText } from '@pharmac/ui'
import { CustomerGroupsListItemDetails } from './CustomerGroupsListItemDetails'
import { CustomerGroupsListItemMobileDetails } from './CustomerGroupsListItemMobileDetails'
import { isWeekdaySunday, weekdayShortTranslations } from './util/date-utils'

type Props = {
  customerGroup: ExtranetCustomerGroupDto
  schedule: ExtranetCustomerGroupScheduleSlotDto | null
  pouchLayoutId: number | null
}

export const CustomerGroupsListItem = ({ customerGroup, schedule, pouchLayoutId }: Props) => {
  const { key, t } = useLanguage()

  const { minDesktop } = useBreakpoint()

  return (
    <ExtranetTableRow
      expand={
        minDesktop ? (
          <CustomerGroupsListItemDetails
            customerGroup={customerGroup}
            pouchLayoutId={pouchLayoutId}
          />
        ) : (
          <CustomerGroupsListItemMobileDetails
            customerGroup={customerGroup}
            schedule={schedule}
            pouchLayoutId={pouchLayoutId}
          />
        )
      }
      toggleExpand={() => null}
      children={({ isExpanded }) => (
        <>
          <ExtranetTableGroupColumn>
            <ExtranetTableRow>
              <ExtranetTableColumn
                size={2}
                marginSmallLeft
                style={{
                  fontWeight: 500,
                }}
              >
                {customerGroup.groupName}
              </ExtranetTableColumn>
              {minDesktop && (
                <ExtranetTableColumn size={1.2}>
                  <ExtranetContainer
                    flexRow
                    alignCenter
                  >
                    {customerGroup.scheduleSlot === null ? (
                      <>
                        <PharmacIcon
                          icon="xCircle"
                          stroke="red"
                          size="20px"
                        />
                        <PharmacText marginSmallLeft>
                          {t('extranet:customerGroupSettings.scheduleNotSet')}
                        </PharmacText>
                      </>
                    ) : (
                      <>
                        {schedule?.cycleDateIndex !== undefined ? (
                          <>
                            <PharmacBadge
                              variant={
                                isWeekdaySunday(schedule?.cycleDateIndex) ? 'primary' : 'secondary'
                              }
                              marginSmallRight
                            >
                              <PharmacText paddingSmallHorizontal>
                                {t('common:week')}{' '}
                                {isWeekdaySunday(schedule?.cycleDateIndex) ? 1 : 2}
                              </PharmacText>
                            </PharmacBadge>
                            <PharmacText>
                              {t(weekdayShortTranslations(schedule?.deadlineWeekday) as any)}{' '}
                              {schedule?.deadlineTime}
                            </PharmacText>
                          </>
                        ) : (
                          <>-</>
                        )}
                      </>
                    )}
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn>
                  <ExtranetContainer
                    flexRow
                    alignCenter
                  >
                    {customerGroup.nextOrderTime ? (
                      <>{formatDateTimeString(customerGroup.nextOrderTime)}</>
                    ) : (
                      <>
                        <PharmacIcon
                          icon="xCircle"
                          stroke="red"
                          size="20px"
                        />
                        <PharmacText marginSmallLeft>
                          {t('extranet:customerGroupSettings.nextOrderTime.notSet')}
                        </PharmacText>
                      </>
                    )}
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn>
                  <ExtranetContainer
                    flexRow
                    alignCenter
                  >
                    {customerGroup?.pricingProductId ? (
                      <>{key(customerGroup?.productName)}</>
                    ) : (
                      <>
                        <PharmacIcon
                          icon="xCircle"
                          stroke="red"
                          size="20px"
                        />

                        <PharmacText marginSmallLeft>
                          {t('extranet:customerGroupSettings.notSet')}
                        </PharmacText>
                      </>
                    )}
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              {minDesktop && (
                <ExtranetTableColumn>
                  {customerGroup.active ? (
                    <ExtranetContainer
                      flexRow
                      alignCenter
                    >
                      <CheckIcon icon="checkCircle" />
                      <PharmacText>{t('common:active')}</PharmacText>
                    </ExtranetContainer>
                  ) : (
                    <ExtranetContainer
                      flexRow
                      alignCenter
                    >
                      <PharmacIcon
                        icon="xCircle"
                        stroke="lightGrey"
                      />
                      <PharmacText marginSmallLeft>{t('common:passive')}</PharmacText>
                    </ExtranetContainer>
                  )}
                </ExtranetTableColumn>
              )}
              <ExtranetExpandArrowColumn>
                <ExtranetCatalogItemChevron isExpanded={isExpanded} />
              </ExtranetExpandArrowColumn>
            </ExtranetTableRow>
          </ExtranetTableGroupColumn>
        </>
      )}
    />
  )
}
